'use client'
import { memo, useMemo, useState } from 'react'

import type { ThemeSettings } from '@typings'
import {
  RaButtonTravelplan,
  RaContainer,
  RaNavbarInteraction,
  RaNavbarLogo,
  RaNavbarMobile,
} from '@components/core/index.core'
import { SETTINGS } from '@constants/settings.constants'
import { Loop } from '@utilities/helpers/core.helpers'

import { RaNavbarMenu } from '../RaNavbarMenu/RaNavbarMenu'

import styles from './ra-navbar.module.scss'

import { RaButtonWishlist } from '@/components/features/RaButtonWishlist/RaButtonWishlist'

interface RaNavbarProps {
  themeSettings: ThemeSettings
  destination?: string
  forceNavbar?: boolean
  isFamilySite?: boolean
}

export const RaNavbar = memo(
  ({
    themeSettings,
    destination,
    forceNavbar,
    isFamilySite,
  }: RaNavbarProps) => {
    const isRoot =
      !destination || SETTINGS.DISABLE_CART_DOMAINS.includes(destination)
    const [showNavbar, setShowNavbar] = useState(false)
    const [dropdownIsOpen, setDropdownOpen] = useState<string | boolean>(false)

    const { blogname, blogdescription, site_label, menus } = themeSettings

    const renderNavbar = useMemo(() => {
      return !!dropdownIsOpen || forceNavbar || showNavbar
    }, [forceNavbar, dropdownIsOpen, showNavbar])

    return (
      <div className={styles['ra-navbar']}>
        <RaNavbarInteraction
          isOpen={renderNavbar}
          setShowNavbar={setShowNavbar}>
          <div className={styles['ra-navbar__desktop']}>
            <RaContainer>
              <nav className={styles['ra-navbar__wrapper']}>
                <RaNavbarLogo
                  title={blogname}
                  subtitle={blogdescription}
                  logoIcon={site_label}
                  destination={destination}
                  isOpen={renderNavbar}
                  isFamilySite={isFamilySite}
                />
                <div className={styles['ra-navbar__menus']}>
                  {Loop(menus?.primary, (menu, i: number) => (
                    <RaNavbarMenu
                      key={i}
                      index={i}
                      menu={menu}
                      renderNavbar={renderNavbar}
                      dropdownOpen={dropdownIsOpen === menu?.link?.title}
                      setDropdownOpen={setDropdownOpen}
                      initialActive={menus.primary_initial_active}
                    />
                  ))}
                </div>
                {!isRoot ? (
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                    <RaButtonWishlist hasNavbar={renderNavbar} />
                    <RaButtonTravelplan hasNavbar={renderNavbar} />
                  </div>
                ) : (
                  <div style={{ width: 160 }}></div>
                )}
              </nav>
            </RaContainer>
          </div>
          <RaNavbarMobile
            {...themeSettings}
            isRoot={isRoot}
            destination={destination}
            showNavbar={renderNavbar}
            isFamilySite={isFamilySite}
          />
        </RaNavbarInteraction>
      </div>
    )
  }
)

RaNavbar.displayName = 'RaNavbar'
